import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import { ExtensionLink } from "components/core/ExtensionLink";
import { FeatureEnabled } from "components/core/FeatureEnabled";
import { ContentCard } from "components/domain/contents/cards/ContentCard";
import { VotingCard } from "components/domain/contents/cards/VotingCard";
import { SectionScaffold } from "components/domain/contents/sections/SectionScaffold";
import { useMixContentQuery } from "graphql/generated";
import { fireEvent } from "lib/ga";
import { useRouter } from "next/router";
import { useMemo, useRef } from "react";
import { ContentSection } from "types/app";
import { LIMIT } from "utils/buildQueryVariables";

export const OriginalSection = ({
    contentSection,
    variables,
    limit = LIMIT.original,
    className,
    ...props
}: {
    contentSection: ContentSection;
    variables: MixContentQueryVariables;
    limit?: number;
} & { className?: string }) => {
    const router = useRouter();
    const ref = useRef<HTMLDivElement>(null);

    const { data } = useMixContentQuery({
        fetchPolicy: "cache-and-network",
        notifyOnNetworkStatusChange: true,
        variables: {
            ...variables,
            limit,
            offset: 0
        }
    });

    const contents = useMemo(() => (data ? data.mix_content : []), [data]);

    return (
        <SectionScaffold className={className} {...props}>
            <SectionScaffold.Header>
                <SectionScaffold.Title
                    className="text-text-primary dark:text-common-white-main"
                >
                    오리지널 콘텐츠
                </SectionScaffold.Title>
                <SectionScaffold.Controls>
                    <ExtensionLink href="/original">
                        <Button
                            size="small"
                            color="grey"
                            variant="text"
                            endIcon="ChevronRight"
                        >
                            더보기
                        </Button>
                    </ExtensionLink>
                </SectionScaffold.Controls>
            </SectionScaffold.Header>
            <SectionScaffold.Contents>
                <div
                    ref={ref}
                    className={cn(
                        `scroll-snap-x-mandatory grid grid-flow-col gap-6
                        overflow-auto`,
                        "scroll-p-[var(--page-padding)]",
                        "[&::-webkit-scrollbar]:hidden",
                        `lg:max-w-screen lg:-mx-[var(--page-padding)] lg:flex
                        lg:gap-6 lg:px-[var(--page-padding)]`,
                        "md:gap-3"
                    )}
                >
                    {/* Show voting card when there's exactly 1 original content, otherwise show both */}
                    {contents.length === 1 ? (
                        <FeatureEnabled
                            featureFlag="SHOW_VOTING_CARD"
                            fallback={
                                contents[0] && (
                                    <ContentCard
                                        className="hidden w-80 snap-start
                                            md:block"
                                        key={contents[0].id}
                                        type={
                                            contents[0].released
                                                ? "original"
                                                : "upcoming"
                                        }
                                        device="web+tablet"
                                        content={contents[0]}
                                        onClick={() => {
                                            const content = contents[0];
                                            if (!content) return;

                                            const params = {
                                                content_section: contentSection,
                                                content_url:
                                                    content.originalurl,
                                                content_isoriginal: true,
                                                content_released:
                                                    content.released
                                            };

                                            fireEvent({
                                                event: "content_click",
                                                params: {
                                                    content_id: content.id,
                                                    content_title:
                                                        content.title,
                                                    ...params
                                                }
                                            });
                                        }}
                                    />
                                )
                            }
                        >
                            <VotingCard className="hidden snap-start md:block" />
                        </FeatureEnabled>
                    ) : (
                        <>
                            {/* Voting Card (desktop/tablet only) */}
                            <FeatureEnabled featureFlag="SHOW_VOTING_CARD">
                                <VotingCard
                                    className="hidden snap-start md:block"
                                />
                            </FeatureEnabled>

                            {/* Show only first content card */}
                            {contents.length > 0 && contents[0] && (
                                <ContentCard
                                    className="w-80 snap-start"
                                    key={contents[0].id}
                                    type={
                                        contents[0].released
                                            ? "original"
                                            : "upcoming"
                                    }
                                    device="web+tablet"
                                    content={contents[0]}
                                    onClick={() => {
                                        const content = contents[0];
                                        if (!content) return;

                                        const params = {
                                            content_section: contentSection,
                                            content_url: content.originalurl,
                                            content_isoriginal: true,
                                            content_released: content.released
                                        };

                                        fireEvent({
                                            event: "content_click",
                                            params: {
                                                content_id: content.id,
                                                content_title: content.title,
                                                ...params
                                            }
                                        });

                                        // window.fbq("track", "ViewContent", {
                                        //   ...params,
                                        //   content_ids: [content.id],
                                        //   content_name: content.title,
                                        //   content_type: "product",
                                        // });
                                    }}
                                />
                            )}
                        </>
                    )}
                </div>
            </SectionScaffold.Contents>
        </SectionScaffold>
    );
};
